import React from 'react';

/* ─────────────────────────────────────────────────────────────────
   "SURGE" — concept: mobile fitness / workout-tracking app
   A fixed, self-contained visual identity — does not follow the
   site's light/dark theme, by design.

   Pass `full` to render the complete, scrollable screen (used
   inside the concept modal, phone-frame); omit it for the card
   preview.
───────────────────────────────────────────────────────────────── */

const WORKOUTS = [
  { name: 'Morning Run', meta: '5.2 km · 27 min', color: '#FF4D6D' },
  { name: 'Upper Body', meta: '45 min · 6 sets', color: '#4C8DFF' },
];

const MORE_WORKOUTS = [
  { name: 'Evening Yoga', meta: '20 min · Recovery', color: '#7ED07E' },
  { name: 'HIIT Circuit', meta: '30 min · 4 rounds', color: '#F2B84C' },
];

const WEEK = [
  { day: 'M', pct: 40 }, { day: 'T', pct: 65 }, { day: 'W', pct: 30 },
  { day: 'T', pct: 80 }, { day: 'F', pct: 55 }, { day: 'S', pct: 90 }, { day: 'S', pct: 20 },
];

const BADGES = ['🔥 7-day streak', '🏅 50 workouts', '⚡ New PB'];

const ProgressRing = ({ pct = 72 }) => {
  const r = 34, c = 2 * Math.PI * r;
  const offset = c - (pct / 100) * c;
  return (
    <svg viewBox="0 0 80 80" className="srg-ring">
      <circle cx="40" cy="40" r={r} className="srg-ring-track" fill="none" />
      <circle
        cx="40" cy="40" r={r} className="srg-ring-progress" fill="none"
        strokeDasharray={c} strokeDashoffset={offset} transform="rotate(-90 40 40)"
      />
      <text x="40" y="36" textAnchor="middle" className="srg-ring-pct">{pct}%</text>
      <text x="40" y="50" textAnchor="middle" className="srg-ring-label">TO GOAL</text>
    </svg>
  );
};

const NavIcon = ({ active }) => (
  <span className={`srg-navdot ${active ? 'srg-navdot-active' : ''}`} />
);

const SurgeApp = ({ full = false }) => (
  <div className={`srg-frame ${full ? 'srg-frame-full' : ''}`}>
    {!full && <div className="srg-notch" />}
    <div className="srg-screen">
      <div className="srg-header">
        <p className="srg-greeting">Good morning,</p>
        <h3 className="srg-name">Aditi</h3>
      </div>

      <div className="srg-ring-card">
        <ProgressRing pct={72} />
        <div className="srg-ring-stats">
          <div><p className="srg-stat-value">6,240</p><p className="srg-stat-label">STEPS</p></div>
          <div><p className="srg-stat-value">412</p><p className="srg-stat-label">KCAL</p></div>
          <div><p className="srg-stat-value">38</p><p className="srg-stat-label">MIN</p></div>
        </div>
      </div>

      {full && (
        <>
          <p className="srg-section-label">This Week</p>
          <div className="srg-week-chart">
            {WEEK.map((d, i) => (
              <div key={i} className="srg-week-col">
                <div className="srg-week-bar-track">
                  <div className="srg-week-bar" style={{ height: `${d.pct}%` }} />
                </div>
                <span className="srg-week-day">{d.day}</span>
              </div>
            ))}
          </div>

          <div className="srg-badges">
            {BADGES.map(b => <span key={b} className="srg-badge">{b}</span>)}
          </div>
        </>
      )}

      <p className="srg-section-label">Today's Workouts</p>
      <div className="srg-workouts">
        {WORKOUTS.map(w => (
          <div key={w.name} className="srg-workout">
            <span className="srg-workout-dot" style={{ background: w.color }} />
            <div>
              <p className="srg-workout-name">{w.name}</p>
              <p className="srg-workout-meta">{w.meta}</p>
            </div>
          </div>
        ))}
        {full && MORE_WORKOUTS.map(w => (
          <div key={w.name} className="srg-workout">
            <span className="srg-workout-dot" style={{ background: w.color }} />
            <div>
              <p className="srg-workout-name">{w.name}</p>
              <p className="srg-workout-meta">{w.meta}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="srg-tabbar">
        <NavIcon active />
        <NavIcon />
        <NavIcon />
        <NavIcon />
      </div>
    </div>

    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Archivo+Black&family=Manrope:wght@400;500;600;700;800&display=swap');

      .srg-frame {
        --bg: #151417; --panel: #1F1E22; --border: #2B2A2F;
        --text: #F5F4F2; --muted: #8B8A90; --accent: #FF4D6D;
        width: 100%; height: 100%; display: flex; flex-direction: column;
        background: var(--bg); border-radius: 26px; overflow: hidden;
        box-shadow: 0 20px 50px rgba(0,0,0,0.4); font-family: 'Manrope', sans-serif;
      }
      .srg-frame-full { height: auto; min-height: 100%; border-radius: 0; box-shadow: none; }
      .srg-notch { width: 70px; height: 16px; background: #000; border-radius: 0 0 10px 10px; margin: 0 auto; }
      .srg-screen { flex: 1; padding: 10px 20px 0; display: flex; flex-direction: column; min-height: 0; }
      .srg-frame-full .srg-screen { padding: 20px 20px 0; }

      .srg-header { margin-bottom: 14px; }
      .srg-greeting { font-size: 12px; color: var(--muted); margin: 0; }
      .srg-name { font-family: 'Archivo Black', sans-serif; font-size: 22px; color: var(--text); margin: 2px 0 0; text-wrap: balance; }

      .srg-ring-card { background: var(--panel); border: 1px solid var(--border); border-radius: 16px; padding: 14px; display: flex; align-items: center; gap: 14px; margin-bottom: 16px; }
      .srg-ring { width: 78px; height: 78px; flex-shrink: 0; }
      .srg-ring-track { stroke: var(--border); stroke-width: 7; }
      .srg-ring-progress { stroke: var(--accent); stroke-width: 7; stroke-linecap: round; transition: stroke-dashoffset 0.6s ease; }
      .srg-ring-pct { fill: var(--text); font-size: 15px; font-weight: 800; font-family: 'Manrope', sans-serif; }
      .srg-ring-label { fill: var(--muted); font-size: 6px; font-weight: 700; letter-spacing: 0.08em; }
      .srg-ring-stats { display: flex; flex-direction: column; gap: 8px; flex: 1; }
      .srg-stat-value { font-variant-numeric: tabular-nums; font-size: 15px; font-weight: 800; color: var(--text); margin: 0; }
      .srg-stat-label { font-size: 9px; color: var(--muted); letter-spacing: 0.08em; margin: 1px 0 0; font-weight: 700; }

      .srg-section-label { font-size: 11px; font-weight: 700; color: var(--muted); letter-spacing: 0.06em; margin: 0 0 10px; }

      .srg-week-chart { display: flex; justify-content: space-between; gap: 8px; background: var(--panel); border: 1px solid var(--border); border-radius: 14px; padding: 14px 12px 10px; margin-bottom: 16px; }
      .srg-week-col { display: flex; flex-direction: column; align-items: center; gap: 8px; flex: 1; }
      .srg-week-bar-track { width: 100%; height: 64px; display: flex; align-items: flex-end; }
      .srg-week-bar { width: 100%; background: var(--accent); border-radius: 4px; min-height: 4px; }
      .srg-week-day { font-size: 9.5px; color: var(--muted); font-weight: 700; }

      .srg-badges { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 16px; }
      .srg-badge { font-size: 10.5px; color: var(--text); background: var(--panel); border: 1px solid var(--border); padding: 6px 11px; border-radius: 999px; }

      .srg-workouts { display: flex; flex-direction: column; gap: 10px; }
      .srg-workout { display: flex; align-items: center; gap: 12px; background: var(--panel); border: 1px solid var(--border); border-radius: 12px; padding: 10px 12px; }
      .srg-workout-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
      .srg-workout-name { font-size: 12.5px; font-weight: 700; color: var(--text); margin: 0 0 2px; }
      .srg-workout-meta { font-size: 10.5px; color: var(--muted); margin: 0; }

      .srg-tabbar { display: flex; justify-content: center; gap: 22px; padding: 14px 0; margin-top: 16px; border-top: 1px solid var(--border); background: var(--bg); position: sticky; bottom: 0; }
    `}</style>
  </div>
);

export default SurgeApp;
